/**
 * useShellAreaPreview - the selected weapon's template, for the map (v8.372.5).
 *
 * The block at its EXPECTATION (dice sizes averaged), who it catches as a
 * predicate - the aimed-at target always, the secondary's own `catches`
 * strict (v8.373.5), since a spawned copy is aimed at nobody - and the dice
 * behind its sizes for the overlay's label. The attack itself rolls the
 * sizes; the preview cannot, so it draws the mean and says so.
 *
 * Returns `{ previewArea, previewEligible, previewSpawnEligible, previewSizeHint }`.
 * Extracted from DeathWatchSimulator.
 */
import { useMemo } from 'react';
import { areaOf, expectedArea, sizeExpression } from '../engine/areaShapes';
import { catchPredicate, sideTagOf } from './useShellAreaTargeting';

/** Which of a block's fields are sizes, per shape. */
const SIZE_FIELDS = {
  cone: (b) => [b.angle, b.length],
  blast: (b) => [b.radius],
  beam: (b) => [b.length, b.width],
  arc: (b) => [b.reach, b.leaps],
};

/** The dice behind a weapon's template sizes ("2d10 · 3"), or '' for none. */
export function sizeHintOf(slot) {
  if (!slot) return '';
  const block = areaOf(slot);
  const fields = SIZE_FIELDS[block.shape]?.(block) ?? [];
  return fields.map(sizeExpression).filter(Boolean).join(' · ');
}

/**
 * @param {object} args - slot (the armed weapon, or undefined), actor, activeTab, roster.
 */
export default function useShellAreaPreview({ slot, actor, activeTab, roster }) {
  const previewArea = useMemo(() => (slot ? expectedArea(areaOf(slot), slot.specialRules) : null), [slot]);
  const attackerTeam = sideTagOf(actor);
  const previewEligible = useMemo(
    () => (previewArea ? catchPredicate(previewArea.catches, attackerTeam, roster, activeTab) : undefined),
    [previewArea, attackerTeam, activeTab, roster],
  );
  const spawn = previewArea?.spawn;
  const previewSpawnEligible = useMemo(
    () => (spawn ? catchPredicate(spawn.catches, attackerTeam, roster) : undefined),
    [spawn, attackerTeam, roster],
  );
  const previewSizeHint = useMemo(() => sizeHintOf(slot), [slot]);
  return { previewArea, previewEligible, previewSpawnEligible, previewSizeHint };
}
